"use client";

import { useEffect, useState } from "react";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowLeftRight,
  Gift,
  Droplets,
  Rocket,
  ExternalLink,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { fetchActivity, type ActivityEntry } from "@/services/activity.service";
import { getTxExplorerUrl } from "@/lib/hoops-contracts";
import {
  formatUSDBalance,
  formatTokenBalance,
} from "@/hooks/useSmartAccountBalances";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

type ActivityFilter = "all" | "deposit" | "swap" | "withdraw";

const ACTIVITY_FILTERS: { key: ActivityFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "deposit", label: "Deposits" },
  { key: "swap", label: "Swaps" },
  { key: "withdraw", label: "Withdrawals" },
];

const ITEMS_PER_PAGE = 10;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${String(m)}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${String(h)}h ago`;
  const d = Math.floor(h / 24);
  return `${String(d)}d ago`;
}

function actionColor(action: ActivityEntry["action"]): string {
  switch (action) {
    case "deposit": return "#10b981";
    case "withdraw": return "#f59e0b";
    case "swap": return "#3b82f6";
    case "claim": return "#c8ff5e";
    case "fund": return "#38bdf8";
    case "deploy": return "#a78bfa";
    default: return "var(--text-secondary)";
  }
}

function actionLabel(action: ActivityEntry["action"]): string {
  switch (action) {
    case "deposit": return "Deposit";
    case "withdraw": return "Withdraw";
    case "swap": return "Swap";
    case "claim": return "Claim";
    case "fund": return "Fund";
    case "deploy": return "Deploy";
    default: return action;
  }
}

function actionIcon(action: ActivityEntry["action"]) {
  switch (action) {
    case "deposit": return ArrowDownToLine;
    case "withdraw": return ArrowUpFromLine;
    case "swap": return ArrowLeftRight;
    case "claim": return Gift;
    case "fund": return Droplets;
    case "deploy": return Rocket;
    default: return ArrowLeftRight;
  }
}

function primaryAmount(entry: ActivityEntry): { value: number | null; symbol: string } {
  const a = entry.amounts;
  if (!a) return { value: null, symbol: "" };
  if (a.usdc != null) return { value: a.usdc, symbol: "USDC" };
  if (a.xlm != null) return { value: a.xlm, symbol: "XLM" };
  if (a.aquaLp != null) return { value: a.aquaLp, symbol: "AQUA LP" };
  if (a.soroswapLp != null) return { value: a.soroswapLp, symbol: "SORO LP" };
  return { value: null, symbol: "" };
}

function formatActivityAmount(entry: ActivityEntry): string | null {
  const { value, symbol } = primaryAmount(entry);
  if (value === null) return null;

  const formatted =
    symbol === "USDC"
      ? formatUSDBalance(value)
      : `${formatTokenBalance(value)} ${symbol}`;

  if (entry.action === "deposit" || entry.action === "fund") return `+${formatted}`;
  if (entry.action === "withdraw") return `-${formatted}`;
  return formatted;
}

// ---------------------------------------------------------------------------
// ActivityRow
// ---------------------------------------------------------------------------

function ActivityRow({ entry }: { entry: ActivityEntry }) {
  const color = actionColor(entry.action);
  const label = actionLabel(entry.action);
  const amountStr = formatActivityAmount(entry);
  const isPositive = entry.action === "deposit" || entry.action === "fund";
  const isNegative = entry.action === "withdraw";
  const Icon = actionIcon(entry.action);
  const txUrl = entry.txHash ? getTxExplorerUrl(entry.txHash) : null;

  return (
    <div
      className="flex items-center gap-3 py-2.5 px-3 rounded-lg transition-colors hover:bg-[var(--surface-hi)]"
      role="listitem"
    >
      {/* Icon circle */}
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}1a` }}
        aria-hidden="true"
      >
        <Icon size={14} style={{ color }} />
      </div>

      {/* Label + timestamp */}
      <div className="flex-1 min-w-0">
        <p className="text-[12px] font-medium" style={{ color: "var(--text-primary)" }}>
          {label}
        </p>
        <p className="text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>
          {relativeTime(entry.timestamp)}
        </p>
      </div>

      {/* Amount + tx link */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {amountStr && (
          <p
            className="text-[12px] font-mono font-medium"
            style={{
              color: isPositive
                ? "var(--green)"
                : isNegative
                  ? "#f87171"
                  : "var(--text-primary)",
            }}
          >
            {amountStr}
          </p>
        )}
        {txUrl && (
          <a
            href={txUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:opacity-80 transition-opacity"
            aria-label="View on Stellar Expert"
          >
            <ExternalLink size={12} style={{ color: "var(--text-tertiary)" }} />
          </a>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ActivitySection
// ---------------------------------------------------------------------------

export function ActivitySection() {
  const [all, setAll] = useState<ActivityEntry[]>([]);
  const [filter, setFilter] = useState<ActivityFilter>("all");
  const [page, setPage] = useState(1);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    fetchActivity()
      .then((data) => { setAll(data); })
      .catch(() => { setLoadError(true); });
  }, []);

  const filtered = all.filter((e) => {
    if (filter === "all") return true;
    if (filter === "deposit") return e.action === "deposit";
    if (filter === "swap") return e.action === "swap";
    return e.action === "withdraw";
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / ITEMS_PER_PAGE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  const handleFilterChange = (f: ActivityFilter) => {
    setFilter(f);
    setPage(1);
  };

  return (
    <section aria-label="Recent activity">
      {/* Section header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2
            className="font-serif font-normal"
            style={{ fontSize: "1.1rem", color: "var(--text-primary)" }}
          >
            Recent Activity
          </h2>
          <p className="text-[11px] mt-0.5" style={{ color: "var(--text-secondary)" }}>
            Transaction history for your smart account
          </p>
        </div>

        {/* Filter tabs */}
        <div
          className="flex items-center gap-1 p-1 rounded-lg"
          style={{ background: "var(--surface)", border: "1px solid var(--border-subtle)" }}
          role="group"
          aria-label="Filter activity by type"
        >
          {ACTIVITY_FILTERS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => { handleFilterChange(key); }}
              className={cn(
                "text-[11px] font-medium px-3 py-1 rounded transition-colors",
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent)]",
              )}
              style={{
                background: filter === key ? "var(--accent)" : "transparent",
                color: filter === key ? "#0a0a0a" : "var(--text-secondary)",
              }}
              aria-pressed={filter === key}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Activity list */}
      <div
        className="rounded-xl overflow-hidden"
        style={{ background: "var(--surface)", border: "1px solid var(--border-default)" }}
      >
        {loadError ? (
          <div className="py-10 text-center" role="alert">
            <p className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
              Unable to load activity. Please try again later.
            </p>
          </div>
        ) : pageItems.length === 0 ? (
          <div className="py-10 text-center" role="status">
            <p className="text-[12px]" style={{ color: "var(--text-secondary)" }}>
              No activity yet
            </p>
          </div>
        ) : (
          <div className="p-2" role="list" aria-label="Activity entries">
            {pageItems.map((entry) => (
              <ActivityRow key={entry.txHash} entry={entry} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {filtered.length > ITEMS_PER_PAGE && (
          <div
            className="flex items-center justify-center gap-4 py-3 px-4"
            style={{ borderTop: "1px solid var(--border-subtle)" }}
          >
            <button
              type="button"
              onClick={() => { setPage((p) => Math.max(1, p - 1)); }}
              disabled={currentPage === 1}
              className={cn(
                "text-[11px] transition-colors",
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent)] rounded",
                currentPage === 1 ? "opacity-30 cursor-not-allowed" : "hover:opacity-70",
              )}
              style={{ color: "var(--text-secondary)" }}
              aria-label="Previous page"
            >
              ← Previous
            </button>

            <span className="text-[11px]" style={{ color: "var(--text-tertiary)" }}>
              Page {currentPage} of {totalPages}
            </span>

            <button
              type="button"
              onClick={() => { setPage((p) => Math.min(totalPages, p + 1)); }}
              disabled={currentPage === totalPages}
              className={cn(
                "text-[11px] transition-colors",
                "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--accent)] rounded",
                currentPage === totalPages ? "opacity-30 cursor-not-allowed" : "hover:opacity-70",
              )}
              style={{ color: "var(--text-secondary)" }}
              aria-label="Next page"
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
